import { FC, JSX, useMemo } from "react";
import { MarketplaceMetalTier, MarketplaceMetalTierCount } from "@app/overviews/market-expansion-dashboard/shared/MarketplaceCountyPlanCounts";
import { METAL_TIER_DISPLAY_CONFIG, METAL_TIER_ORDER } from "@app/overviews/market-expansion-dashboard/MarketExpansionDashboardConstants";

interface MetalTierSegment {
  color: string;
  label: string;
  percentage: number;
  planCount: number;
  tier: MarketplaceMetalTier;
}

interface MarketplaceMetalTierBarProps {
  metalTiers: MarketplaceMetalTierCount[];
}

const MarketplaceMetalTierBar: FC<MarketplaceMetalTierBarProps> = ({ metalTiers }): JSX.Element => {
  const segments = useMemo<MetalTierSegment[]>(() => {
    const totalPlanCount = metalTiers.reduce((sum, tier) => sum + tier.planCount, 0);

    if (totalPlanCount === 0) {
      return [];
    }

    const planCountByTier = new Map(metalTiers.map((tier) => [tier.metalLevel, tier.planCount]));

    return METAL_TIER_ORDER
      .map((tier) => ({
        color: METAL_TIER_DISPLAY_CONFIG[tier].color,
        label: METAL_TIER_DISPLAY_CONFIG[tier].label,
        percentage: ((planCountByTier.get(tier) ?? 0) / totalPlanCount) * 100,
        planCount: planCountByTier.get(tier) ?? 0,
        tier,
      }))
      .filter((segment) => segment.planCount > 0);
  }, [metalTiers]);

  if (!segments.length) {
    return <></>;
  }

  return (
    <div className="metal-tier-bar" data-testid="metal-tier-bar">
      <div className="metal-tier-bar__bar">
        {segments.map((segment) => (
          <div
            key={segment.tier}
            className="metal-tier-bar__segment"
            style={{ backgroundColor: segment.color, width: `${segment.percentage}%` }}
            title={`${segment.label}: ${segment.percentage.toFixed(0)}%`}
            data-testid={`metal-tier-bar__segment-${segment.tier}`}
          />
        ))}
      </div>
      <div className="metal-tier-bar__legend">
        {segments.map((segment) => (
          <div key={segment.tier} className="metal-tier-bar__legend-item" data-testid={`metal-tier-bar__legend-item-${segment.tier}`}>
            <span className="metal-tier-bar__legend-swatch" style={{ backgroundColor: segment.color }} />
            <span className="metal-tier-bar__legend-label">
              {segment.label} <span className="metal-tier-bar__legend-value">{segment.percentage.toFixed(0)}%</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MarketplaceMetalTierBar;
